import { useNavigate } from "react-router-dom";
import { IconPackage, IconPackageImport } from "@tabler/icons-react";
import { Chip } from "../../../../components/common/chip";
import { Text } from "../../../../components/common/typography";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { ProductRequestDto } from "../../../../api";
import { ProductRequestDtoStatusEnum } from "../../../../api";
import { useAdminDashboardProductApprovals } from "../../hooks/useAdminDashboardProductApprovals";
import { DashboardSectionCard } from "./dashboard-section-card";

type ApprovalItem = Pick<ProductRequestDto, "id" | "name" | "status" | "image">;

function statusChip(status: ApprovalItem["status"]) {
  if (status === ProductRequestDtoStatusEnum.Approved) return <Chip variant="approved">Approved</Chip>;
  if (status === ProductRequestDtoStatusEnum.Rejected) return <Chip variant="rejected">Rejected</Chip>;
  return <Chip variant="inReview">Pending</Chip>;
}

function ProductApprovalItem({ row, onClick }: { row: ApprovalItem; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border border-borderDefault bg-bgWhite px-4 py-3 text-left transition-colors hover:border-borderDark hover:bg-bgLight"
    >
      {row.image ? (
        <img src={row.image} alt={row.name} className="size-10 shrink-0 rounded-lg object-cover" />
      ) : (
        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-bgLight text-textMuted">
          <IconPackage size={20} />
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <Text variant="bodySmBold" tone="dark">{row.name}</Text>
        {/* TODO: backend to expose categoryName and sellerName on ProductRequestDto list endpoint */}
        <Text variant="bodyXs" tone="muted">—</Text>
      </div>
      {statusChip(row.status)}
    </button>
  );
}

export function PendingProductApprovalsCard() {
  const navigate = useNavigate();
  const { data, isLoading } = useAdminDashboardProductApprovals();
  const items = ((data?.data as ApprovalItem[] | undefined) ?? []).slice(0, 3);

  return (
    <DashboardSectionCard title="Pending Product Approvals" viewAllRoute={AppRoutes.ADMIN_PRODUCT_REQUESTS}>
      {isLoading ? (
        <ShimmerLoader message="Loading product approvals…" lines={3} />
      ) : items.length === 0 ? (
        <EmptyState
          size="sm"
          icon={IconPackageImport}
          title="No products waiting"
          description="All product requests have been reviewed."
        />
      ) : (
          items.map((row) => (
          <ProductApprovalItem
            key={row.id}
            row={row}
            onClick={() =>
              navigate(AppRoutes.ADMIN_PRODUCT_REQUEST_DETAILS.replace(":id", String(row.id)))
            }
          />
        ))
      )}
    </DashboardSectionCard>
  );
}
